import { gql } from '@soundxyz/gql-string';
import { usePostsEnabled } from '@components/Feed/Post/usePostsEnabled';
import { useAuth } from '@context/auth/base';
import type { OnData } from '@graphql/graphql-ws';
import { useSubscription } from '@graphql/graphql-ws';
import {
  NewGlobalUserNotificationDocument,
  NewUserNotificationDocument,
  useInfiniteQuery,
  UserNotificationsHookDocument,
} from '@graphql/reactQuery';
import { CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY } from '@utils/constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  subscription NewUserNotification {
    notificationUpdates: userNotificationUpdates {
      notificationTimestamp
    }
  }

  subscription NewGlobalUserNotification {
    notificationUpdates: globalUserNotificationUpdates {
      notificationTimestamp
    }
  }

  # TODO: https://linear.app/sound/issue/ENG-8582/remove-feature-flag-in-usenotificationhook
  query UserNotificationsHook($pagination: CursorConnectionArgs!, $feedV2Flag: Boolean!) {
    userNotifications(pagination: $pagination) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          __typename
          id
          timestamp
          ... on SubscribeToEmailCTA {
            id
          }
          ... on GoldenEggWon {
            ...GoldenEggWinnerNotif
          }
          ... on NewFollower {
            ...NewFollowerNotif
          }
          ... on PresaleStarted {
            ...PresaleArtistDropNotif
          }
          ... on PublicSaleStarted {
            ...ArtistDropNotif
          }
          ... on ReferralReceivedNotification {
            ...ReferralReceivedNotif
          }
          ... on ReleaseMinted {
            ...ReleaseMintedNotif
          }
          ... on ViralMintsReached {
            ...ViralMintNotif
          }
          ... on NewPostComment @include(if: $feedV2Flag) {
            ...NewPostCommentNotif
          }
          ... on NewPostUpvote @include(if: $feedV2Flag) {
            ...PostUpvotedNotif
          }
          ... on ArtistReleasePosted @include(if: $feedV2Flag) {
            ...ArtistReleasePostedNotif
          }
        }
      }
    }
  }
`);

export function useUserNotifications() {
  const { authUser } = useAuth();
  const authUserId = authUser?.id;
  const { viewingPostsEnabled } = usePostsEnabled();
  const {
    orderedList: data,
    isLoading,
    isFetchingNextPage,
    loadMoreNextPage: loadMore,
    hasNextPage = false,
    isInitialLoading,
    refetch,
  } = useInfiniteQuery(UserNotificationsHookDocument, {
    getNextPageParam({ data: { userNotifications } }) {
      return (
        userNotifications?.pageInfo.hasNextPage && {
          after: userNotifications.pageInfo.endCursor,
        }
      );
    },
    list({ userNotifications }) {
      return userNotifications?.edges.map(({ node }) => node);
    },
    uniq({ id }) {
      return id;
    },
    filterQueryKey: {
      authUserId,
    },
    enabled: !!authUserId,
    variables({ pageParam }) {
      const after = pageParam?.after || null;
      return {
        pagination: {
          first: after == null ? 20 : 15,
          after,
        },
        feedV2Flag: viewingPostsEnabled,
      };
    },
    // Don't refetch on window focus to make sure we don't refetch and have the pop up button visible
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  });

  return {
    data,
    isLoading,
    isInitialLoading,
    isFetchingNextPage,
    loadMore,
    hasNextPage,
    refetch,
  };
}

export function useUserNotificationSubscriptions({
  onNewGlobalUserNotificationSubscribeData,
  onNewUserNotificationSubscribeData,
}: {
  onNewGlobalUserNotificationSubscribeData: OnData<typeof NewGlobalUserNotificationDocument>;
  onNewUserNotificationSubscribeData: OnData<typeof NewUserNotificationDocument>;
}) {
  const { isAuthenticatedWithAPI } = useAuth();
  useSubscription({
    query: NewUserNotificationDocument,
    enabled: !!isAuthenticatedWithAPI,
    onData: onNewUserNotificationSubscribeData,
  });

  useSubscription({
    query: NewGlobalUserNotificationDocument,
    enabled: !!isAuthenticatedWithAPI,
    onData: onNewGlobalUserNotificationSubscribeData,
  });
}
